import * as React from "react";
import { ExportOutlined } from "@ant-design/icons";
import { Button, ButtonProps } from "antd5";
import classnames from "classnames";

import { useUTMParams, UTMParams } from "lib/providers/UTMParams";
import { EventNames, useTracking } from "lib/tracking";
import TextButton from "./TextButton";

import css from "./Links.module.scss";

export type TargetType =
  | "same-tab" // the default mode
  | "new-tab" // open in new tab (both internal and external links)
  | "external"; // open in new tab and show "external" icon unless specified not to

export type TextLinkProps = React.PropsWithChildren<{
  to: string;
  targetType?: TargetType; // same-tab by default
  disabled?: boolean;
  eventName?: EventNames;
  eventAttributes?: Record<string, string | string[] | Record<string, string[]> | undefined | null>;
  className?: string;
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  showIcon?: boolean; // only relevant for external links, defaults to true
  skipUtmParams?: boolean;
  download?: string;
  authorised?: boolean;
  onUnauthorisedClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  secondary?: boolean;
  ellipsis?: boolean; // whether to apply ellipsis to the text if it overflows
  noreferrer?: boolean; // if true, adds noreferrer to rel attribute to prevent referrer from being sent
  ref?: (instance: HTMLElement | null) => void;
}>;

function isInternalLink(link: string) {
  return (
    link.includes("stotles.com") ||
    link.includes(window.location.origin) ||
    link[0] === "/" ||
    link[0] === "#"
  );
}

export function getLinkDestination(loc: string, utmParams?: UTMParams): string {
  let link = "";
  if (loc.startsWith("http") || loc.startsWith("mailto") || loc.startsWith("blob")) {
    link = loc;
  } else if (loc[0] === "/" || loc[0] === "#") {
    link = window.location.origin + loc;
  } else {
    link = "http://" + loc;
  }

  if (utmParams && isInternalLink(link)) {
    const params = new URLSearchParams(utmParams as Record<string, string>).toString();
    let url;
    try {
      url = new URL(link);
    } catch (_) {
      // URL throws on invalid url - fallback just in case
      return link;
    }

    if (url.search.length) {
      url.search += `&${params}`;
    } else {
      url.search += `?${params}`;
    }
    link = url.toString();
  }

  return link;
}

export function TextLink(props: TextLinkProps): JSX.Element {
  const {
    to,
    eventName,
    eventAttributes,
    className,
    onClick,
    targetType,
    showIcon = true,
    skipUtmParams,
    authorised = true,
    onUnauthorisedClick,
    secondary = false,
    ellipsis = false,
    noreferrer = false,
    ...others
  } = props;
  const { logEvent } = useTracking();
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (eventName) logEvent(eventName, eventAttributes);
      if (onClick) onClick(e);
      e.stopPropagation();
    },
    [eventName, logEvent, eventAttributes, onClick],
  );

  const handleAuxClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      // Only handle the middle button
      if (e.button === 1) {
        handleClick(e);
      }
    },
    [handleClick],
  );
  const target = targetType === "new-tab" || targetType === "external" ? "_blank" : "_self";

  const utmParams = useUTMParams()?.utmParams;
  const linkDestination = others.disabled
    ? "#"
    : getLinkDestination(to, skipUtmParams ? undefined : utmParams);
  const rel = noreferrer ? "noopener noreferrer" : "noopener";

  const baseClassName = !secondary ? css.textLink : css.secondaryTextLink;

  if (!authorised) {
    return (
      <TextButton
        className={classnames(className, baseClassName, { [css.ellipsis]: ellipsis })}
        onClick={onUnauthorisedClick}
        {...others}
      >
        {props.children}
      </TextButton>
    );
  }

  return (
    <a
      className={classnames(className, baseClassName, { [css.ellipsis]: ellipsis })}
      target={target}
      href={linkDestination}
      rel={rel}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      {...others}
    >
      {props.children}
      {targetType === "external" && showIcon && <ExportOutlined className={css.externalLinkIcon} />}
    </a>
  );
}

export type ButtonLinkProps = ButtonProps & TextLinkProps;

export function ButtonLink({
  to,
  targetType,
  eventName,
  eventAttributes,
  onClick,
  showIcon = true,
  authorised = true,
  onUnauthorisedClick,
  children,
  className,
  secondary = false,
  ellipsis = false,
  noreferrer = false,
  ...others
}: ButtonLinkProps): JSX.Element {
  const { logEvent } = useTracking();
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement & HTMLButtonElement, MouseEvent>) => {
      if (eventName) logEvent(eventName, eventAttributes);
      if (onClick) onClick(e);
      e.stopPropagation();
    },
    [eventName, logEvent, eventAttributes, onClick],
  );

  const handleAuxClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement & HTMLButtonElement, MouseEvent>) => {
      // Only handle the middle button
      if (e.button === 1) {
        handleClick(e);
      }
    },
    [handleClick],
  );

  const baseClassName = !secondary ? css.textLink : css.secondaryTextLink;

  if (!authorised) {
    return (
      <Button
        className={classnames(className, baseClassName, { [css.ellipsis]: ellipsis })}
        onClick={onUnauthorisedClick}
        {...others}
      >
        {children}
      </Button>
    );
  }

  const target = targetType === "new-tab" || targetType === "external" ? "_blank" : "_self";
  const linkDestination = others.disabled ? "#" : getLinkDestination(to);
  const rel = noreferrer ? "noopener noreferrer" : "noopener";

  return (
    <Button
      href={linkDestination}
      target={target}
      rel={rel}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      {...others}
    >
      {children}
      {targetType === "external" && showIcon && <ExportOutlined className={css.externalLinkIcon} />}
    </Button>
  );
}
